// Helpers
import {
  to,
  cleanPhoneNumber,
  convertNullsToEmptyStrings,
  stripDashes,
} from '../../lib/helpers';
import {
  bodyHasError,
  checkRequestFailure,
  handleRequestFailure,
  ERRORS_MAP,
  errorsWithOnlyCodes,
} from '../../lib/validation';
import { gaActions } from '../gaActionTypes';

// Requests
import {
  checkAccountRecoveryEmailRequest,
  accountVerifyRequest,
  accountDeactivateRequest,
} from '../../backend/accountRecoveryRequests';
import { userVerifyAddressRequest } from '../../backend/userRequests';

// Actions
import { push } from 'connected-react-router';
import { openModal, closeCurrentModal } from '../modal/modalActions';
import { clearAlertInfo } from '../alert/alertActions';

// Consts
export const ACCOUNT_RECOVERY_STORE_EMAIL = `[ACCOUNT_RECOVERY] ${gaActions.submit} Store Email`;
export const ACCOUNT_RECOVERY_STORE_IDENTITY = `[ACCOUNT_RECOVERY] ${gaActions.submit} Store Identity`;
export const ACCOUNT_RECOVERY_CAPTURE_HOME_ADDRESS = `[ACCOUNT_RECOVERY] ${gaActions.submit} Capture Home Address`;
export const ACCOUNT_RECOVERY_STORE_VERIFICATION = `[ACCOUNT_RECOVERY] ${gaActions.submit} Store Verification`;
export const ACCOUNT_RECOVERY_CLEAR_STATE = '[ACCOUNT_RECOVERY] Clear State';

export const ACCOUNT_RECOVERY_STEPS = {
  EMAIL: 'email',
  IDENTITY: 'identity',
  ADDRESS: 'address',
  VERIFICATION_DLID: 'verification-dlid',
  VERIFICATION_PHONE: 'verification-phone',
  SUCCESS: 'success',
  DEACTIVATED: 'deactivated',
  FAILED_IDPROOFING: 'idproof-failed',
  IDPROOFING_MAX_ATTEMPTS: 'idproof-attempts-exceeded',
  DISABLED_USER: 'disabled-user',
  ACCOUNT_NOT_FOUND: 'account-not-found',
};

export const submitEmail = ({ values, formikActions }) => async dispatch => {
  const { email } = values;

  const [{ response, body }] = await to(
    dispatch(checkAccountRecoveryEmailRequest(email))
  );

  formikActions.setSubmitting(false);
  dispatch(clearAlertInfo());

  dispatch({
    type: ACCOUNT_RECOVERY_STORE_EMAIL,
    payload: { email },
  });

  if (checkRequestFailure({ response, body })) {
    return handleRequestFailure(body, formikActions, ERRORS_MAP.ADDRESS);
  }

  if (body.message === 'Masked phone number retrieved') {
    dispatch(openModal('CheckPhoneAccessModal', { maskedPhone: body.data }));
  } else {
    dispatch(push('/account-recovery/identity'));
  }
};

export const continueToIdentity = () => dispatch => {
  dispatch(push('/account-recovery/identity'));
};

export const submitIdentity = ({ values, formikActions }) => async dispatch => {
  const user = { fName: values.fName, lName: values.lName };
  const pii = { dob: values.dob, ssn: values.ssn };

  dispatch({
    type: ACCOUNT_RECOVERY_STORE_IDENTITY,
    payload: { user, pii },
  });

  formikActions.setSubmitting(false);
  dispatch(push('/account-recovery/address'));
};

export const captureAddress = ({ values, formikActions }) => async dispatch => {
  const addressValues = convertNullsToEmptyStrings(values);

  if (values.verifySkip) {
    await dispatch({
      type: ACCOUNT_RECOVERY_CAPTURE_HOME_ADDRESS,
      payload: addressValues,
    });
    dispatch(closeCurrentModal());
    formikActions.setSubmitting(false);
    return dispatch(verifyUser(formikActions));
  }

  const [{ response, body }] = await to(
    dispatch(userVerifyAddressRequest(addressValues))
  );

  if (body.status === 'ERROR' && !bodyHasError(body)) {
    if (['INVALID_ADDRESS', 'ADDRESS_NOT_VERIFIED'].includes(body.message)) {
      body.status = 'SUCCESS';
      body.data = [addressValues];
    } else {
      const errorId =
        response.headersObject && response.headersObject['x-req-id'];
      body.errors = {
        ...(errorId && { REF: [errorId] }),
        ERROR: ['ACCOUNT_RECOVERY_ERROR'],
      };
    }
  }

  if (checkRequestFailure({ response, body })) {
    return handleRequestFailure(body, formikActions, ERRORS_MAP.ADDRESS);
  }

  if (body.status === 'CONTINUE') {
    formikActions.setSubmitting(false);
    return dispatch(
      openModal('VerifyAddressModal/AccountRecoveryPage', {
        enteredAddress: addressValues,
        addressOptions: body.data,
      })
    );
  }

  if (body.status === 'WARNING') {
    formikActions.setSubmitting(false);
    return dispatch(
      openModal('AddressTwoMissingModal/AccountRecoveryPage', {
        enteredAddress: addressValues,
        addressOptions: body.data,
      })
    );
  }

  if (body.status === 'SUCCESS') {
    await dispatch({
      type: ACCOUNT_RECOVERY_CAPTURE_HOME_ADDRESS,
      payload: { ...body.data[0], verifySkip: true },
    });
    dispatch(closeCurrentModal());
    formikActions.setSubmitting(false);
    return dispatch(verifyUser(formikActions));
  }
};

const verifyUser = formikActions => async (dispatch, getStore) => {
  const { email, user, pii, homeAddress } = getStore().accountRecovery;

  const accountVerifyPayload = {
    userEmail: email,
    firstName: user.fName,
    lastName: user.lName,
    pii: {
      dob: pii.dob,
      ssn: stripDashes(pii.ssn),
      address: {
        ...homeAddress,
        deliveryPointCode: '09',
        savePrimaryAsMailing: true,
        addressType: 'PrimaryAddress',
      },
    },
  };

  const [{ response, body }] = await to(
    dispatch(accountVerifyRequest(accountVerifyPayload))
  );

  if (checkRequestFailure({ response, body })) {
    const errorCodes = errorsWithOnlyCodes(((body && body.errors) || {}).ERROR);

    let errorPage;
    if (
      errorCodes.includes('MGMW_E_0404') ||
      errorCodes.includes('MGMW_E_0004')
    ) {
      errorPage = ACCOUNT_RECOVERY_STEPS.FAILED_IDPROOFING;
    }
    if (errorCodes.includes('MGMW_E_0031')) {
      dispatch({ type: ACCOUNT_RECOVERY_CLEAR_STATE });
      errorPage = ACCOUNT_RECOVERY_STEPS.IDPROOFING_MAX_ATTEMPTS;
    }
    if (errorCodes.includes('MGMW_EX_9997')) {
      errorPage = ACCOUNT_RECOVERY_STEPS.DISABLED_USER;
    }
    if (errorCodes.includes('MGMW_E_0001')) {
      errorPage = ACCOUNT_RECOVERY_STEPS.ACCOUNT_NOT_FOUND;
    }

    if (errorPage) {
      formikActions.setSubmitting(false);
      return dispatch(push(`/account-recovery/${errorPage}`));
    } else {
      return handleRequestFailure(body, formikActions);
    }
  }

  dispatch({
    type: ACCOUNT_RECOVERY_STORE_VERIFICATION,
    payload: {
      userSessionToken: body.message,
      userPiiToken: body?.data?.userPiiToken,
    },
  });

  if (body?.data?.phoneExists) {
    dispatch(
      push(`/account-recovery/${ACCOUNT_RECOVERY_STEPS.VERIFICATION_PHONE}`)
    );
  } else if (body?.data?.dlExists) {
    dispatch(
      push(`/account-recovery/${ACCOUNT_RECOVERY_STEPS.VERIFICATION_DLID}`)
    );
  } else if (body.status === 'CONTINUE') {
    dispatch(
      push(`/account-recovery/${ACCOUNT_RECOVERY_STEPS.SUCCESS}`)
    );
  }
};

export const submitVerification = ({ values, formikActions }) => async (
  dispatch,
  getStore
) => {
  const { email, verification } = getStore().accountRecovery;

  let verificationRequestData = {};
  if (values.hasDLID) {
    verificationRequestData = {
      userEmail: email,
      pii: {
        dlNumber: values.dlNumber,
        auditNum: values.auditNum,
      },
    };
  } else {
    verificationRequestData = {
      userEmail: email,
      phoneNumber: cleanPhoneNumber(values.phoneNum),
    };
  }

  const { userSessionToken, userPiiToken } = verification;

  const [{ response, body }] = await to(
    dispatch(
      accountVerifyRequest({
        userSessionToken,
        userPiiToken,
        ...verificationRequestData,
      })
    )
  );

  const maskedEmail = body?.data?.maskedEmail;

  if (maskedEmail) {
    dispatch(openModal('CheckEmailAccessModal', { maskedEmail }));
    formikActions.setSubmitting(false);
    return;
  }

  if (checkRequestFailure({ response, body })) {
    const errorCodes = errorsWithOnlyCodes(((body && body.errors) || {}).ERROR);

    if (errorCodes.includes('MGMW_E_0033')) {
      formikActions.setSubmitting(false);
      return dispatch(openModal('AccountRecoveryTokenExpiredModal'));
    }

    let errorPage;

    if (errorCodes.includes('MGMW_EX_9997')) {
      errorPage = ACCOUNT_RECOVERY_STEPS.DISABLED_USER;
    }
    if (errorCodes.includes('MGMW_E_0001')) {
      errorPage = ACCOUNT_RECOVERY_STEPS.ACCOUNT_NOT_FOUND;
    }
    if (
      errorCodes.includes('MGMW_E_0404') ||
      errorCodes.includes('MGMW_E_0004')
    ) {
      errorPage = ACCOUNT_RECOVERY_STEPS.FAILED_IDPROOFING;
    }

    if (errorPage) {
      formikActions.setSubmitting(false);
      return dispatch(push(`/account-recovery/${errorPage}`));
    } else {
      return handleRequestFailure(body, formikActions);
    }
  }

  dispatch({
    type: ACCOUNT_RECOVERY_STORE_VERIFICATION,
    payload: { hasDLID: values.hasDLID },
  });

  formikActions.setSubmitting(false);
  return dispatch(push('/account-recovery/success'));
};

export const submitDeactivation = () => async (dispatch, getStore) => {
  const { userSessionToken } = getStore().accountRecovery.verification;
  const [{ response, body }, err] = await to(
    dispatch(accountDeactivateRequest({ userSessionToken }))
  );

  if (!body && !err) {
    return false;
  }

  const errorCodes = errorsWithOnlyCodes(((body && body.errors) || {}).ERROR);

  if (errorCodes.includes('MGMW_E_0033')) {
    dispatch(openModal('AccountRecoveryTokenExpiredModal'));
    return Promise.resolve();
  }

  if (response.status !== 200) {
    return Promise.reject({ response, body });
  }

  dispatch(push('/account-recovery/deactivated'));

  return Promise.resolve({ response, body });
};

export const clearAccountRecoveryState = () => ({
  type: ACCOUNT_RECOVERY_CLEAR_STATE,
});
